"use client";

import DocLayout, { DocSection } from "../../components/DocLayout";

export default function Privacy() {
  return (
    <DocLayout title="Privacy Policy" subtitle="Last updated: May 2026">
      <DocSection title="The short version">
        <p>
          Your data is yours. We store what you give us so your second brain works.
          We don{"\u2019"}t sell your data. We don{"\u2019"}t train on your data. You can export
          everything and delete your account at any time.
        </p>
        <p style={{ marginTop: "var(--space-3)" }}>
          F{"\u00FC"}lkit knows how you think. Not what you think.
        </p>
      </DocSection>

      <DocSection title="The five promises">
        <ul style={{ paddingLeft: "var(--space-5)", display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
          <li><strong>No selling.</strong> We don{"\u2019"}t sell your data. Not to advertisers, not to brokers, not to anyone.</li>
          <li><strong>No training.</strong> We don{"\u2019"}t use your conversations or notes to train AI models. Not ours, not anyone else{"\u2019"}s.</li>
          <li><strong>No third-party sharing.</strong> Your content stays inside F{"\u00FC"}lkit unless you explicitly connect an integration you chose.</li>
          <li><strong>Delete works.</strong> Click delete, it{"\u2019"}s gone. Atomic cascade. We wrote the SQL.</li>
          <li><strong>Export works.</strong> Take your notes with you in plain markdown any time. We never lock the door behind you.</li>
        </ul>
      </DocSection>

      <DocSection title="What Fülkit holds, and why">
        <p>
          F{"\u00FC"}lkit{"\u2019"}s job is to remember the shape of your life so the AI can be useful in conversation.
          That means we hold the structure of how you work {"\u2014"} habits, patterns, the cadence of your week,
          the integrations you{"\u2019"}ve connected {"\u2014"} so the assistant can show up prepared instead of asking
          you to re-explain yourself every time.
        </p>
        <p style={{ marginTop: "var(--space-3)" }}>
          The content of your private notes doesn{"\u2019"}t need to live with us forever for the brain to work.
          When the AI helps you, it reads what{"\u2019"}s relevant in the moment {"\u2014"} like opening a notebook {"\u2014"}
          and moves on.
        </p>
      </DocSection>

      <DocSection title="Two ways to live with Fülkit">
        <p>
          <strong>Default mode:</strong> your vault lives with us in encrypted Supabase storage. The full AI brain
          works {"\u2014"} search, suggestions, save-on-the-fly. This is the simplest and fastest experience and
          works on every device including mobile.
        </p>
        <p style={{ marginTop: "var(--space-3)" }}>
          <strong>Local Vault mode:</strong> your notes live in a folder of plain markdown files on your own
          computer. F{"\u00FC"}lkit reads them at chat-time to help you, never stores them on our servers. Sync
          across devices with your own Dropbox, iCloud, or Google Drive {"\u2014"} F{"\u00FC"}lkit just reads wherever
          the folder lives. Desktop only (Chrome, Edge, Arc, or Brave).
        </p>
        <p style={{ marginTop: "var(--space-3)" }}>
          Either way: you always have a copy of your files in plain markdown. If you ever leave, every file
          comes with you.
        </p>
      </DocSection>

      <DocSection title="The honest line">
        <p>
          On Default mode, we could read your notes. We choose not to. Here{"\u2019"}s exactly why you should believe us:
        </p>
        <ul style={{ paddingLeft: "var(--space-5)", display: "flex", flexDirection: "column", gap: "var(--space-2)", marginTop: "var(--space-3)" }}>
          <li>Our business model is your $9/month subscription, not your data. Selling it would torch the business.</li>
          <li>The architecture is documented {"\u2014"} read the receipts. Every query is scoped by your user ID at the database level.</li>
          <li>If reading your notes was the product, we wouldn{"\u2019"}t also offer Local Vault mode where we can{"\u2019"}t.</li>
          <li>We{"\u2019"}re a small team. Solo founder. No board pushing growth-at-all-costs. Reputation is the asset.</li>
        </ul>
      </DocSection>

      <DocSection title="What we collect">
        <ul style={{ paddingLeft: "var(--space-5)", display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
          <li>Account info (name, email via Google sign-in)</li>
          <li>Notes and documents you upload or create</li>
          <li>AI conversation history</li>
          <li>Preferences you set (display, AI behavior, vault mode)</li>
          <li>A referral cookie if you arrived via a referral link (30-day expiry)</li>
          <li>Usage data (message counts, feature usage)</li>
        </ul>
      </DocSection>

      <DocSection title="How we use it">
        <p>
          To make your second brain work. Your notes are embedded and stored so
          the AI can retrieve them. Your preferences are learned so the AI can
          talk to you the way you want. That's it.
        </p>
      </DocSection>

      <DocSection title="How long we keep it">
        <p>
          Different data has different lifespans. Here is what stays, what
          rotates, and what disappears when you ask:
        </p>
        <ul style={{ paddingLeft: "var(--space-5)", display: "flex", flexDirection: "column", gap: "var(--space-2)", marginTop: "var(--space-2)" }}>
          <li><strong>Notes you delete:</strong> soft-deleted for 30 days, then permanently removed. During the 30-day window your deleted notes have zero influence on search, suggestions, or any AI behavior {"—"} they are excluded from every index.</li>
          <li><strong>Notes you keep:</strong> retained for the life of your account.</li>
          <li><strong>AI conversation history:</strong> retained while your account is active so context carries forward; deletable per-conversation or in bulk from Settings.</li>
          <li><strong>Preferences and account profile:</strong> retained while your account is active; deleted on account closure.</li>
          <li><strong>OAuth tokens (Google, Spotify, GitHub, etc.):</strong> stored encrypted while the integration is connected; deleted within 24 hours of disconnect.</li>
          <li><strong>Referral cookie:</strong> 30-day expiry, set in your browser only; never written to our servers.</li>
          <li><strong>Usage data (message counts, feature events):</strong> retained for billing and product analytics for 24 months, then aggregated/anonymized.</li>
          <li><strong>Account closure:</strong> when you delete your account, all data above is purged within 30 days. Backup retention on Supabase infrastructure may extend up to 30 days beyond that, after which residual copies are unrecoverable.</li>
        </ul>
      </DocSection>

      <DocSection title="Third parties">
        <ul style={{ paddingLeft: "var(--space-5)", display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
          <li>Anthropic (Claude API) — processes your messages, does not retain them</li>
          <li>Voyage AI — generates note embeddings for semantic search, does not store content</li>
          <li>Supabase — database and authentication hosting</li>
          <li>Vercel — application hosting and serverless functions</li>
          <li>Stripe — payment processing and referral payouts</li>
          <li>Upstash — distributed rate limiting (Redis)</li>
          <li>Google — OAuth sign-in and connected services (Calendar, Gmail, Drive) when enabled by you</li>
          <li>Spotify, GitHub, Square, Stripe, Shopify, Toast, Trello, Fitbit, QuickBooks, Notion, Dropbox, Slack, OneNote, Todoist, Readwise — only when connected by you, for the features you enable</li>
          <li>Invisible intelligence APIs (server-side, no user data shared): OpenWeatherMap (weather), WAQI (air quality), USDA &amp; Open Food Facts (nutrition), Open Library (books), Frankfurter (currency), Free Dictionary API, Nominatim (geocoding), Wikipedia, NASA (APOD), Wolfram Alpha, Currents (news), Have I Been Pwned (breach check)</li>
        </ul>
      </DocSection>

      <DocSection title="Google user data">
        <p>
          When you connect Google services (Calendar, Gmail, Drive), F{"\u00FC"}lkit
          requests only the scopes needed for what you asked for:
        </p>
        <ul style={{ paddingLeft: "var(--space-5)", display: "flex", flexDirection: "column", gap: "var(--space-2)", marginTop: "var(--space-2)" }}>
          <li><strong>Calendar</strong> ({"\u201C"}calendar.events{"\u201D"}) — read upcoming events and availability for chat context, create events when you ask</li>
          <li><strong>Gmail</strong> ({"\u201C"}gmail.readonly{"\u201D"}) — search and read email threads to answer your questions. Read-only. F{"\u00FC"}lkit never sends, deletes, or modifies email.</li>
          <li><strong>Drive</strong> ({"\u201C"}drive.readonly{"\u201D"} and {"\u201C"}drive.file{"\u201D"}) — find and read documents you choose to import to your vault</li>
        </ul>
        <p style={{ marginTop: "var(--space-3)" }}>
          Google user data is processed in-context to answer your immediate
          question and is not retained beyond that. It is never used to train AI
          models, never shared with third parties (including Anthropic), and never
          sold. OAuth tokens are stored encrypted and deleted when you disconnect
          the integration.
        </p>
        <p style={{ marginTop: "var(--space-3)" }}>
          F{"\u00FC"}lkit{"\u2019"}s use and transfer of information received from Google APIs
          to any other app will adhere to{" "}
          <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noopener noreferrer" style={{ color: "var(--color-text)", textDecoration: "underline" }}>
            Google API Services User Data Policy
          </a>
          , including the Limited Use requirements.
        </p>
      </DocSection>

      <DocSection title="Cookies and similar technologies">
        <p>
          F{"ü"}lkit uses cookies and browser storage only for what makes the product work. No
          ad networks, no third-party analytics tracking, no cross-site tracking.
        </p>
        <ul style={{ paddingLeft: "var(--space-5)", display: "flex", flexDirection: "column", gap: "var(--space-2)", marginTop: "var(--space-2)" }}>
          <li><strong>Session cookies:</strong> set by Supabase to keep you signed in. Cleared on sign-out.</li>
          <li><strong>Referral cookie:</strong> set if you arrived via a {"“"}Get F{"ü"}lkit{"”"} referral link. 30-day expiry. Browser-only.</li>
          <li><strong>localStorage preferences:</strong> your storage mode (Default vs Local Vault), compact-mode toggle, last-active tab. Browser-only; never sent to us.</li>
        </ul>
        <p style={{ marginTop: "var(--space-3)" }}>
          You can clear these from your browser settings any time. F{"ü"}lkit functions
          without them; you{"’"}ll just need to sign in again.
        </p>
      </DocSection>

      <DocSection title="Automated decision-making and AI">
        <p>
          F{"ü"}lkit uses AI to generate responses, suggest content, surface patterns,
          and produce daily digests. These are conversational and informational
          {"—"} F{"ü"}lkit does not make consequential automated decisions about you
          (employment, credit, housing, insurance, eligibility for any legal benefit).
          You can override, edit, or ignore any AI output, and you can delete the
          history that informs it.
        </p>
      </DocSection>

      <DocSection title="Children">
        <p>
          F{"ü"}lkit is not directed at children under 13 and we don{"’"}t knowingly
          collect data from anyone under 13. If you believe a child under 13 has
          provided data to F{"ü"}lkit, contact us at privacy@fulkit.app and we
          will delete it. Users between 13 and the age of majority in their state
          should have parent or guardian consent to use the service.
        </p>
      </DocSection>

      <DocSection title="US state privacy rights">
        <p>
          F{"ü"}lkit is offered to US residents and complies with applicable US state
          privacy laws including the California Consumer Privacy Act (CCPA), the
          Virginia Consumer Data Protection Act (VCDPA), the Colorado Privacy Act
          (CPA), the Connecticut Data Privacy Act (CTDPA), and the Utah Consumer
          Privacy Act (UCPA). Regardless of which state you live in, you have these
          rights:
        </p>
        <ul style={{ paddingLeft: "var(--space-5)", display: "flex", flexDirection: "column", gap: "var(--space-2)", marginTop: "var(--space-2)" }}>
          <li><strong>Right to know.</strong> Request a copy of the personal data F{"ü"}lkit holds about you.</li>
          <li><strong>Right to delete.</strong> Request deletion of your personal data, subject to legal exceptions (e.g., billing records).</li>
          <li><strong>Right to correct.</strong> Update inaccurate personal data.</li>
          <li><strong>Right to opt out.</strong> F{"ü"}lkit does not sell personal data and does not use it for cross-context behavioral advertising; there is nothing to opt out of, but you can confirm this in writing on request.</li>
          <li><strong>Right to non-discrimination.</strong> Exercising your rights does not affect your access to F{"ü"}lkit, your pricing, or your service quality.</li>
          <li><strong>Authorized agent.</strong> California residents may designate an authorized agent to make requests on their behalf with written authorization.</li>
        </ul>
        <p style={{ marginTop: "var(--space-3)" }}>
          To exercise any of these rights, email privacy@fulkit.app from the
          address associated with your account, or use the built-in export and
          delete tools in Settings. F{"ü"}lkit responds within 45 days; an
          additional 45-day extension may apply where permitted by law.
        </p>
      </DocSection>

      <DocSection title="Plus One accounts">
        <p>
          F{"ü"}lkit Plus One lets two consenting adults share one account. The
          account-holder is the primary controller of the account and its data.
          Both users{"’"} activity contributes to the shared brain. Either user
          can export or delete shared data; account closure is initiated by the
          account-holder.
        </p>
      </DocSection>

      <DocSection title="Sister products">
        <p>
          This Privacy Policy covers F{"ü"}lkit only. Other Colsha LLC products
          (Numbrly, TrueGauge) and HB Beverage Co. (HBBEVCO LLC) operate under
          their own privacy policies on their respective sites. Connecting one of
          those products to F{"ü"}lkit shares only what the integration
          specifies, on the same terms as any other connected source.
        </p>
      </DocSection>

      <DocSection title="Subprocessors and changes">
        <p>
          The third-party services listed above process data on F{"ü"}lkit{"’"}s
          behalf. F{"ü"}lkit may add or change subprocessors over time as the
          product evolves; the current list is always on this page. Material
          changes are reflected here with an updated {"\""}Last updated{"\""} date and,
          when the change affects how user data is processed, surfaced in-product.
        </p>
      </DocSection>

      <DocSection title="Security and breach notification">
        <p>
          F{"ü"}lkit uses encryption at rest (Supabase database-level), HTTPS in
          transit, encrypted OAuth token storage, row-level security scoping every
          query to your user ID, and signed URLs for any file delivery. No system
          is perfectly secure; if F{"ü"}lkit becomes aware of a personal-data
          breach affecting you, we will notify you without unreasonable delay and
          in compliance with applicable law.
        </p>
      </DocSection>

      <DocSection title="Your rights">
        <p>
          Export all your data at any time from Settings. Delete
          specific memories, conversations, or your entire account. No lock-in,
          ever.
        </p>
      </DocSection>

      <DocSection title="Contact">
        <p>
          Questions about privacy? Email{" "}
          <a
            href="mailto:privacy@fulkit.app"
            style={{
              color: "var(--color-text)",
              fontWeight: "var(--font-weight-semibold)",
              textDecoration: "underline",
            }}
          >
            privacy@fulkit.app
          </a>
          .
        </p>
      </DocSection>
    </DocLayout>
  );
}
